import cx from 'classnames/bind';
import React, { useRef } from 'react';
import styles from './MiniScreen.scss';
import { FocusTrapZone } from 'focus-trap-zone';
import { PortalWrapper } from 'portal-wrapper';
import { useApplySafeAreaPadding } from 'mini-hooks/lib/useApplySafeAreaPadding';
import type {
    MiniScreenProps,
    MiniScreenSlotInternalProps,
    MiniScreenSlotProps,
    MiniScreenSubComponents,
    SlotType,
} from '../schema/MiniScreen.types';

const classNames = cx.bind(styles);

const PossibleSlotTypes: SlotType[] = ['MiniScreenHeader', 'MiniScreenBody', 'MiniScreenFooter'];

const getWrapperClassNameFromProps = (props: MiniScreenProps) =>
    classNames(styles.miniScreen, styles.miniScreenWrapper, props.className);

const Slot: React.FC<MiniScreenSlotInternalProps> = props =>
    props.children && (
        <div className={classNames(props.defaultClassName, props.className)}>{props.children}</div>
    );

/**
 * The header slot
 * @param props
 */
const Header: React.FC<MiniScreenSlotProps> = props => (
    <Slot defaultClassName={styles.header} {...props} />
);

Header.defaultProps = {
    type: 'MiniScreenHeader',
};

/**
 * The body slot
 * @param props
 */
const Body: React.FC<MiniScreenSlotProps> = props => (
    <Slot defaultClassName={styles.body} {...props} />
);

Body.defaultProps = {
    type: 'MiniScreenBody',
};

/**
 * The footer slot
 * @param props
 */
const Footer: React.FC<MiniScreenSlotProps> = props => (
    <Slot defaultClassName={styles.footer} {...props} />
);

Footer.defaultProps = {
    type: 'MiniScreenFooter',
};

// The inner content needed
// Whichever be the order of te children sent, only those marked with type Header, footer and body will be accomodated
// and the order will be sorted based on the order present in PossibleSlotTypes so that Header comes first, body after that and then footer
const InnerContent: React.FC<MiniScreenProps> = props =>
    props.children && (
        <>
            {React.Children.count(props.children) === 1
                ? props.children
                : React.Children.map(props.children, child => {
                      if (
                          React.isValidElement(child) &&
                          !!child.props &&
                          PossibleSlotTypes.indexOf(child.props.type) > -1
                      ) {
                          return child;
                      } else {
                          throw new Error(
                              `Children of the MiniScreen should have type property with values ${PossibleSlotTypes.join(
                                  ','
                              )} `
                          );
                      }
                  }).sort(
                      (child1, child2) =>
                          PossibleSlotTypes.indexOf(child1.props.type) -
                          PossibleSlotTypes.indexOf(child2.props.type)
                  )}
        </>
    );

/**
 * Component to create the Screen component with the header, body and footer, all optional
 */
const MiniScreenInternal: MiniScreenSubComponents<MiniScreenProps> = props => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    const safeAreaRef = useRef<any>(null);
    useApplySafeAreaPadding(safeAreaRef, props.noSafeArea);
    return (
        <div className={getWrapperClassNameFromProps(props)} ref={safeAreaRef}>
            <InnerContent {...props} />
        </div>
    );
};

/**
 * Component to create a mini screen outside the current div
 * @param props
 */
const MiniScreenPortal: MiniScreenSubComponents<MiniScreenProps> = props => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    const safeAreaRef = useRef<any>(null);
    useApplySafeAreaPadding(safeAreaRef, props.noSafeArea);
    return (
        <PortalWrapper divOrIdToAttachTo={props.id}>
            <div className={getWrapperClassNameFromProps(props)} ref={safeAreaRef}>
                <InnerContent {...props} />
            </div>
        </PortalWrapper>
    );
};

MiniScreenPortal.Header = Header;
MiniScreenPortal.Body = Body;
MiniScreenPortal.Footer = Footer;

/**
 * Component to create a screen in the mobile which also traps focus
 * @param props
 */
const MiniScreen: MiniScreenSubComponents<MiniScreenProps> = props =>
    props.trapFocus ? (
        <FocusTrapZone
            id={props.id}
            hideBackground
            focusOnOpen={props.focusOnOpen}
            focusOnClose={props.focusOnClose}>
            <MiniScreenPortal {...props} />
        </FocusTrapZone>
    ) : (
        <MiniScreenInternal {...props} />
    );

MiniScreen.Header = Header;
MiniScreen.Body = Body;
MiniScreen.Footer = Footer;

MiniScreen.defaultProps = { trapFocus: true };

export { MiniScreen, MiniScreenPortal };
